import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Skeleton,
  Table, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import ShieldRoundedIcon         from "@mui/icons-material/ShieldRounded";
import BarChartRoundedIcon        from "@mui/icons-material/BarChartRounded";
import AccountBalanceRoundedIcon  from "@mui/icons-material/AccountBalanceRounded";
import SettingsRoundedIcon        from "@mui/icons-material/SettingsRounded";
import StarRoundedIcon            from "@mui/icons-material/StarRounded";
import WarningAmberRoundedIcon    from "@mui/icons-material/WarningAmberRounded";
import { useNavigate } from "react-router-dom";
import { getData } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  violet:  "#7C3AED", violetLt: "#EDE9FE",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

const PIE_PALETTE = [T.teal, T.orange, T.indigo, T.emerald, T.amber, T.rose, T.violet, "#0891B2"];

const STATUS_COLORS = {
  filed:            T.indigo,
  under_review:     T.amber,
  evidence_pending: T.rose,
  approved:         T.emerald,
  rejected:         T.rose,
  closed:           T.muted,
};

const fmt = (n) => n != null ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "0";
const fmtRs = (n) => `₹${fmt(n)}`;

// ── Navigation tabs ───────────────────────────────────────────────────────
const NAV_TABS = [
  { label:"Claims",    icon:<ShieldRoundedIcon />,        path:"/app/protection-claims",    idx:0 },
  { label:"Analytics", icon:<BarChartRoundedIcon />,      path:"/app/protection-analytics", idx:1 },
  { label:"Fund",      icon:<AccountBalanceRoundedIcon />, path:"/app/protection-fund",      idx:2 },
  { label:"Config",    icon:<SettingsRoundedIcon />,       path:"/app/protection-config",    idx:3 },
];

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, accent, loading, sub }) => (
  <Card sx={{ flex:"1 1 140px", borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
    <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />
    <CardContent sx={{ p:2, "&:last-child":{ pb:2 }, textAlign:"center" }}>
      {loading ? <Skeleton width={80} height={28} sx={{ mx:"auto" }} /> :
        <Typography sx={{ fontSize:22, fontWeight:800, color:accent, lineHeight:1.1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{value}</Typography>
      }
      <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", mt:0.8 }}>{label}</Typography>
      {sub && <Typography sx={{ fontSize:10, color:accent, mt:0.3 }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

// ── Chart card ────────────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, children, height=280 }) => (
  <Card sx={{ flex:"1 1 300px", borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)" }}>
    <CardContent sx={{ p:2.5, "&:last-child":{ pb:2.5 } }}>
      <Typography sx={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif", mb:subtitle?0.3:1.5 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize:12, color:T.muted, mb:1.5 }}>{subtitle}</Typography>}
      <Box sx={{ height }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

// ── Custom chart tooltip ──────────────────────────────────────────────────
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor:"#fff", border:`1px solid ${T.border}`, borderRadius:"10px", p:1.5, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", minWidth:130 }}>
      {label && <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, mb:0.5 }}>{label}</Typography>}
      {payload.map(p => (
        <Box key={p.name} sx={{ display:"flex", alignItems:"center", gap:1 }}>
          <Box sx={{ width:8, height:8, borderRadius:"50%", bgcolor:p.fill||p.color }} />
          <Typography sx={{ fontSize:12, fontWeight:600, color:T.text }}>{p.name}: {p.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Star display ──────────────────────────────────────────────────────────
const Stars = ({ value, max=5 }) => (
  <Box sx={{ display:"flex", gap:0.3 }}>
    {Array.from({length:max}).map((_, i) => (
      <StarRoundedIcon key={i} sx={{ fontSize:18, color: i < Math.round(value||0) ? T.amber : T.border }} />
    ))}
    <Typography sx={{ fontSize:12, fontWeight:700, color:T.amber, ml:0.5 }}>{Number(value||0).toFixed(1)}</Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function ProtectionStats() {
  const navigate = useNavigate();
  const [stats,       setStats]       = useState(null);
  const [ratingStats, setRatingStats] = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [claimRes, ratingRes] = await Promise.all([
          getData(API_ENDPOINTS.getProtectionClaimStats),
          getData(API_ENDPOINTS.getRatingAnalytics),
        ]);
        const cr = claimRes?.result || claimRes?.data?.result;
        if (cr) setStats(cr);
        const rr = ratingRes?.result || ratingRes?.data?.result;
        if (rr) setRatingStats(rr);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  // Chart data
  const statusData = (stats?.byStatus || []).map(s => ({
    name: s.status?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()),
    value: parseInt(s.count),
    fill: STATUS_COLORS[s.status] || T.muted,
  }));

  const categoryData = (stats?.byCategory || []).map((s,i) => ({
    name: s.category?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()),
    value: parseInt(s.count),
    fill: PIE_PALETTE[i % PIE_PALETTE.length],
  }));

  const ratingBarData = (ratingStats?.breakdown_by_category || []).map(c => ({
    category: c.category?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()),
    Outcome: parseFloat(c.avg_outcome||0),
    Process: parseFloat(c.avg_process||0),
  }));

  const fundStatus = stats?.fundHealth?.status;
  const fundColor  = fundStatus==="healthy" ? T.emerald : fundStatus==="warning" ? T.amber : T.rose;

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <BarChartRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cargo Protection</Typography>
            <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.3 }}>Analytics · claims, payouts, ratings and fund health</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Pill nav tabs ── */}
      <Box sx={{ display:"flex", gap:0.5, bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"14px", p:0.75, mb:3, width:"fit-content" }}>
        {NAV_TABS.map(t => (
          <Box key={t.path} onClick={() => navigate(t.path)} sx={{
            display:"flex", alignItems:"center", gap:1,
            px:2.5, py:1.2, borderRadius:"10px", cursor:"pointer",
            bgcolor: t.idx===1 ? T.teal : "transparent",
            color:   t.idx===1 ? "#fff" : T.muted,
            fontWeight: t.idx===1 ? 700 : 500, fontSize:13, transition:"all 0.15s",
            "&:hover":{ bgcolor:t.idx===1?T.teal:T.tealLt, color:t.idx===1?"#fff":T.teal },
            fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}>
            {React.cloneElement(t.icon, { sx:{ fontSize:16 } })}
            {t.label}
          </Box>
        ))}
      </Box>

      {/* ── KPI tiles ── */}
      <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:3 }}>
        <KpiTile label="Total Claims"       value={loading?"—":stats?.totalClaims}                                             accent={T.teal}    loading={loading} />
        <KpiTile label="Total Protections"  value={loading?"—":stats?.totalProtectionBookings}                                 accent={T.indigo}  loading={loading} />
        <KpiTile label="Total Payouts"      value={loading?"—":fmtRs(stats?.totalApprovedPayout||0)}                           accent={T.emerald} loading={loading} />
        <KpiTile label="Avg Payout"         value={loading?"—":fmtRs(stats?.averagePayout||0)}                                 accent={T.amber}   loading={loading} />
        <KpiTile label="Premiums Collected" value={loading?"—":fmtRs(stats?.totalPremiumsCollected||0)}                        accent={T.orange}  loading={loading} />
        <KpiTile label="Fund Balance"       value={loading?"—":fmtRs(stats?.fundHealth?.balance||0)} accent={fundColor}        loading={loading}
          sub={fundStatus ? fundStatus.charAt(0).toUpperCase()+fundStatus.slice(1) : undefined} />
      </Box>

      {/* ── Charts row 1 ── */}
      <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:3 }}>

        {/* Claims by Status donut */}
        <ChartCard title="Claims by Status" subtitle="Distribution across current lifecycle states">
          {statusData.length === 0
            ? <Typography fontSize={13} color="text.secondary">No data</Typography>
            : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="45%" outerRadius={90} innerRadius={45}
                    paddingAngle={3} dataKey="value"
                    label={({percent})=>percent>0.05?`${(percent*100).toFixed(0)}%`:""}
                    labelLine={false}>
                    {statusData.map((d,i) => <Cell key={i} fill={d.fill} stroke="none" />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
                </PieChart>
              </ResponsiveContainer>
            )
          }
        </ChartCard>

        {/* Claims by Category donut */}
        <ChartCard title="Claims by Category" subtitle="Types of cargo incidents reported">
          {categoryData.length === 0
            ? <Typography fontSize={13} color="text.secondary">No data</Typography>
            : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="45%" outerRadius={90} innerRadius={45}
                    paddingAngle={3} dataKey="value"
                    label={({percent})=>percent>0.05?`${(percent*100).toFixed(0)}%`:""}
                    labelLine={false}>
                    {categoryData.map((d,i) => <Cell key={i} fill={d.fill} stroke="none" />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
                </PieChart>
              </ResponsiveContainer>
            )
          }
        </ChartCard>
      </Box>

      {/* ── Rating Analytics ── */}
      {ratingStats && (
        <>
          {/* Rating KPI tiles */}
          <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:3 }}>
            <KpiTile label="Avg Outcome Rating" value={`${ratingStats.avg_outcome_rating||0}/5`} accent={T.amber}  sub="customer satisfaction" />
            <KpiTile label="Avg Process Rating" value={`${ratingStats.avg_process_rating||0}/5`} accent={T.amber}  sub="handling quality" />
            <KpiTile label="Total Ratings"      value={ratingStats.total_ratings||0}              accent={T.indigo} />
            <KpiTile label="Rating Coverage"    value={`${ratingStats.rating_coverage_pct||0}%`}
              accent={ratingStats.rating_coverage_pct>50 ? T.emerald : T.amber}
              sub={ratingStats.rating_coverage_pct>50?"Good coverage":"Low response"} />
          </Box>

          {/* Rating by category bar chart */}
          {ratingBarData.length > 0 && (
            <Box sx={{ mb:3 }}>
              <ChartCard title="Avg Rating by Claim Category" subtitle="Outcome vs Process scores per category" height={240}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ratingBarData} barGap={4} margin={{ top:4, right:8, bottom:0, left:0 }}>
                    <defs>
                      <linearGradient id="gOutcome" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor={T.teal}  stopOpacity={1} />
                        <stop offset="100%" stopColor={T.teal} stopOpacity={0.65} />
                      </linearGradient>
                      <linearGradient id="gProcess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor={T.amber}  stopOpacity={1} />
                        <stop offset="100%" stopColor={T.amber} stopOpacity={0.65} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                    <XAxis dataKey="category" tick={{ fontSize:11, fill:T.muted }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0,5]} tick={{ fontSize:11, fill:T.muted }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill:T.tealLt }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:11, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
                    <Bar dataKey="Outcome" fill="url(#gOutcome)" radius={[6,6,0,0]} barSize={20} />
                    <Bar dataKey="Process" fill="url(#gProcess)" radius={[6,6,0,0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Box>
          )}

          {/* Low-rated claims */}
          {ratingStats.low_rated_claims?.length > 0 && (
            <Card sx={{ borderRadius:"16px", border:`1px solid ${T.rose}44`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)" }}>
              <CardContent sx={{ p:2.5, "&:last-child":{ pb:2.5 } }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1.2, mb:2 }}>
                  <Box sx={{ width:28, height:28, borderRadius:"8px", bgcolor:T.roseLt, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <WarningAmberRoundedIcon sx={{ fontSize:15, color:T.rose }} />
                  </Box>
                  <Typography sx={{ fontSize:14, fontWeight:700, color:T.rose, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    Low-Rated Claims — Needs Review
                  </Typography>
                </Box>
                <Table size="small">
                  <TableBody>
                    {ratingStats.low_rated_claims.map((c,i) => {
                      const avg = ((parseFloat(c.avg_outcome)||0) + (parseFloat(c.avg_process)||0)) / 2;
                      return (
                        <TableRow key={i} sx={{ "&:last-child td":{ border:0 } }}>
                          <TableCell sx={{ fontSize:12, fontWeight:700, color:T.rose, fontFamily:"monospace" }}>{c.claim_ref}</TableCell>
                          <TableCell sx={{ fontSize:12, color:T.muted }}>{c.category?.replace(/_/g," ")}</TableCell>
                          <TableCell><Stars value={avg} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
